import React from "react"
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from "@carrotfertility/carotene-core"
import {
  getAvailableParentIdsByBenefitConfig,
  PARENT_ID_MAPPING,
  PARENT_ID_TO_PROVIDER_COUNT_MAPPING
} from "../../utils/parentIdMapping"
import { useGetHasProviderTypeInCountry } from "../../hooks/useGetHasProviderTypeInCountry"
import { useUserContext } from "../../context/UserContext"
import { FormattedMessage, useIntl } from "react-intl"
import { useIntlSort } from "../../../../utils/hooks/useIntlSort"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { FertilityWraparoundMessageForDropdown } from "../sidebar/FertilityWraparoundMessage"
import {
  GARNER_PROVIDER_TYPE_MAPPPING,
  GarnerProviderType
} from "#/pages/provider-finder/utils/garnerProviderTypeMapping"
import useShowProviderRecommendations from "#/pages/provider-finder/hooks/useShowProviderRecommendations"

type ProviderTypeFilterDropdownProps = {
  selectedProviderType: string
  setSelectedProviderType: React.Dispatch<React.SetStateAction<string>>
  benefit: Benefit
  shouldShowGarnerProviderType: boolean
}

const OPENSEARCH_AND_GARNER_PROVIDER_TYPE_MAPPING = { ...PARENT_ID_MAPPING, ...GARNER_PROVIDER_TYPE_MAPPPING }

function RenderSelectedProviderType(providerType: string): JSX.Element {
  if (!providerType) {
    return (
      <Typography color={(theme) => theme.palette.text.disabled}>
        <FormattedMessage defaultMessage="Select a provider type" />
      </Typography>
    )
  }

  return (
    <Typography component="span" overflow="hidden" textOverflow="ellipsis">
      <FormattedMessage {...OPENSEARCH_AND_GARNER_PROVIDER_TYPE_MAPPING[providerType].combinedDisplayName} />
    </Typography>
  )
}

export function ProviderTypeFilterDropdown({
  selectedProviderType,
  setSelectedProviderType,
  benefit,
  shouldShowGarnerProviderType
}: ProviderTypeFilterDropdownProps): JSX.Element {
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const availableParentIds = getAvailableParentIdsByBenefitConfig(benefit)
  const { companyCountryCode } = useUserContext()
  const { data: hasProviderTypes, isLoading: isLoadingHasProviderTypes } =
    useGetHasProviderTypeInCountry(companyCountryCode)
  const hasFertilityWraparoundEnabled = benefit.providerFinder.showFertilityWrapAroundMessage
  const { data: showProviderRecommendationsData, isLoading: isLoadingShowProviderRecommendations } =
    useShowProviderRecommendations()

  const handleChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value }
    } = event
    setSelectedProviderType(value)
  }
  const opensearchProviderTypeOptions = availableParentIds.filter((parentId) => {
    const providerType = PARENT_ID_TO_PROVIDER_COUNT_MAPPING[parentId]
    return hasProviderTypes && hasProviderTypes[providerType]
  })

  const garnerProviderTypeOptions = [
    showProviderRecommendationsData.showObGynRecommendationsForm ? GarnerProviderType.OBGYN : null,
    showProviderRecommendationsData.showPediatricianRecommendationsForm ? GarnerProviderType.PEDIATRICIAN : null
  ].filter(Boolean)

  const combinedOptions = shouldShowGarnerProviderType
    ? [...opensearchProviderTypeOptions, ...garnerProviderTypeOptions]
    : opensearchProviderTypeOptions
  const sortedOptions = combinedOptions
    .map((id) => ({
      value: id,
      label: intl.formatMessage(OPENSEARCH_AND_GARNER_PROVIDER_TYPE_MAPPING[id].combinedDisplayName)
    }))
    .sort((a, b) => localeComparator(a.label, b.label))
  const optionsElements = sortedOptions.map(({ value, label }) => (
    <MenuItem value={value} key={value}>
      <Box display="flex" flexDirection="column">
        {label}
      </Box>
    </MenuItem>
  ))

  if (isLoadingHasProviderTypes || isLoadingShowProviderRecommendations) {
    return null
  }

  return (
    <FormControl fullWidth>
      <InputLabel id={"provider-type-dropdown-select-label"} required>
        <FormattedMessage defaultMessage="Provider type" />
      </InputLabel>
      <Select
        labelId="provider-type-dropdown-select-label"
        id="provider-type-dropdown-select"
        value={selectedProviderType}
        onChange={handleChange}
        renderValue={RenderSelectedProviderType}
      >
        {optionsElements}
        {hasFertilityWraparoundEnabled && <FertilityWraparoundMessageForDropdown />}
      </Select>
    </FormControl>
  )
}
